import React, { Suspense, useEffect, useState } from 'react';
import LoadingScreen from '../components/layout/LoadingScreen';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthGuard from '../../features/auth/components/AuthGuard';
import UserGuard from '../../features/user/components/UserGuard';
import { CssBaseline, Box, ThemeProvider } from '@mui/material';
import AdminGuard from '../../features/user/components/AdminGuard';
import Layout from '../components/layout/Layout';
import { createGlobalTheme } from '../utils/globalTheme';
import { onTelegramThemeChanged, isTelegramWebApp } from '../utils/telegramUtils';
import WebLayout from '../components/layout/WebLayout';
import MiniAppSwitch from '../components/pages/MiniAppSwitch';
import { useLocation } from 'react-router-dom';
import RouteWithSEO from '../components/layout/RouteWithSEO';

const AppEntryPage = React.lazy(() => import('../components/pages/AppEntryPage'))

const ProjectsPage = React.lazy(() => import('../../features/constructor/components/ProjectsPage'))
const HomePage = React.lazy(() => import('../components/pages/HomePage'));
const MiniAppHomePage = React.lazy(() => import('../components/pages/MiniAppHomePage'))
const ProjectEditorPage = React.lazy(() => import('../../features/constructor/components/ProjectEditorPage'));
const ProjectCreatePage = React.lazy(() => import('../../features/constructor/components/ProjectCreatePage'));
const LinkTelegramPage = React.lazy(() => import('../components/auth/LinkTelegramPage'))
const AppPreviewPage = React.lazy(() => import('../components/pages/AppPreviewPage'))
const AppMiniPage = React.lazy(() => import('../components/pages/AppMiniPage'))
const AppSettingsPage = React.lazy(() => import('../components/pages/AppSettingsPage'))
const MobileSettings = React.lazy(() => import('../../features/miniapp/components/MobileSettings'))
const TemplatesPage = React.lazy(() => import('../../features/admin/templates/components/TemplatesPage'))
const TemplateEditorPage = React.lazy(() => import('../../features/admin/templates/components/TemplateEditorPage'))
const TemplateCreatePage = React.lazy(() => import('../../features/admin/templates/components/TemplateCreatePage'))
const AnalyticsPage = React.lazy(() => import('../components/pages/AnalyticsPage'))
const ShopPage = React.lazy(() => import('../components/pages/ShopPage'))
const StoreSettingsPage = React.lazy(() => import('../../features/miniapp/components/StoreSettingsPage'))
const OrdersPage = React.lazy(() => import('../../features/miniapp/components/OrdersPage'))
const ThemeSettingsPage = React.lazy(() => import('../../features/miniapp/components/ThemeSettingsPage'))
const ProductMobileSettingsPage = React.lazy(() => import('../../features/miniapp/components/ProductMobileSettingsPage'))
const UnderConstructionPage = React.lazy(() => import('../components/pages/UnderConstruction'))
const InstructionPage = React.lazy(() => import('../components/pages/InstructionPage'))
const ScreenEditorPage = React.lazy(() => import('../../features/constructor/components/ScreenEditorPage'))
const NotificationsSettingsPage = React.lazy(() => import('../../features/miniapp/components/NotificationsSettingsPage'))
const NewsPage = React.lazy(() => import('../components/pages/NewsPage'))
const AppLanding = React.lazy(() => import('../components/pages/AppLanding'))
const PricingSelectionPage = React.lazy(() => import('../../features/payments/components/PricingSelectionPage'))
const AffiliateProgramPage = React.lazy(() => import('../../features/affiliate/components/AffiliateProgramPage'))


const AppRouter = () => {
    const [theme, setTheme] = useState(createGlobalTheme());
    const location = useLocation();

    useEffect(() => {
        setTheme(createGlobalTheme());
        const off = onTelegramThemeChanged(() => {
            setTheme(createGlobalTheme());
        });
        return () => off && off();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refId = params.get('ref_id');
        if (refId && !document.cookie.split('; ').some(c => c.startsWith('ref_id='))) {
            document.cookie = `ref_id=${encodeURIComponent(refId)}; path=/; max-age=2147483647; SameSite=Lax`;
        }
    }, [location.search]);

    useEffect(() => {
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('top');
        document.documentElement.style.removeProperty('overflow');
    }, [location.pathname]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Suspense fallback={
                isTelegramWebApp()
                    ? <LoadingScreen text='Загрузка...' />
                    : (
                        <WebLayout>
                            <LoadingScreen text='Загрузка страницы...' />
                        </WebLayout>
                    )
            }>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'calc(100vh - var(--telegram-top-offset, 0px))',
                    bgcolor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                }}>
                    <Routes>
                        <Route path='loading' element={<LoadingScreen />} />
                        <Route path='/' element={
                            <MiniAppSwitch
                                MiniComponent={() => (
                                    <AuthGuard>
                                        <UserGuard>
                                            <MiniAppHomePage />
                                        </UserGuard>
                                    </AuthGuard>
                                )}
                                WebComponent={() => (
                                    <RouteWithSEO
                                        titleRu="Создать приложение без кода — конструктор Fleep Web Apps и Telegram Mini Apps"
                                        titleEn="No-Code App Builder — Build Web Apps & Telegram Mini Apps Without Coding | Fleep"
                                        descriptionRu="Создавайте приложения без кода в конструкторе Fleep. Web Apps и Telegram Mini Apps для бизнеса, продаж и автоматизации."
                                        descriptionEn="Build apps without coding using Fleep. Create Web Apps and Telegram Mini Apps quickly and easily with a powerful no-code app builder."
                                        noindex={false}
                                    >
                                        <AppLanding />
                                    </RouteWithSEO>
                                )}
                                noWebLayout={true}
                            />
                        } />
                        <Route path='/profile' element={
                            <RouteWithSEO
                                titleRu="Мои проекты — Флип.Приложения"
                                titleEn="My Projects — Fleep.Apps"
                                descriptionRu="Управляйте своими мини-приложениями"
                                descriptionEn="Manage your Mini Apps"
                                noindex={true}
                            >
                                <UserGuard>
                                    <MiniAppSwitch MiniComponent={MiniAppHomePage} WebComponent={HomePage} />
                                </UserGuard>
                            </RouteWithSEO>
                        } />
                        {/* <Route path='/app_landing' element={<AppLanding />} /> */}
                        <Route path='/tariff-selection' element={
                            <RouteWithSEO
                                titleRu="Выбор тарифа — Флип.Приложения"
                                titleEn="Pricing Selection — Fleep.Apps"
                                descriptionRu="Выберите подходящий тариф для создания мини-приложения"
                                descriptionEn="Choose the right pricing plan for creating mini app"
                                noindex={true}
                            >
                                <MiniAppSwitch
                                    MiniComponent={() => (
                                        <AuthGuard>
                                            <UserGuard>
                                                <PricingSelectionPage />
                                            </UserGuard>
                                        </AuthGuard>
                                    )}
                                    WebComponent={() => (<div>Page not found</div>)}
                                    noWebLayout
                                />
                            </RouteWithSEO>
                        } />
                        <Route path='/instruction' element={
                            <RouteWithSEO
                                titleRu="Как создать приложение без кода — инструкция конструктора Telegram Mini Apps"
                                titleEn="How to Build an App Without Coding — No-Code App Builder Guide | Fleep"
                                descriptionRu="Узнайте, как создать приложение без программирования. Инструкция по работе с конструктором Fleep Web Apps и Telegram Mini Apps."
                                descriptionEn="Learn how to build an app without coding using Fleep. Step-by-step guide to creating Web Apps and Telegram Mini Apps."
                                noindex={false}
                            >
                                <MiniAppSwitch MiniComponent={InstructionPage} WebComponent={InstructionPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/news' element={
                            <RouteWithSEO
                                titleRu="Новости — Флип.Приложения"
                                titleEn="News — Fleep.Apps"
                                descriptionRu="Последние новости и обновления"
                                descriptionEn="Latest news and updates"
                                noindex={true}
                            >
                                <MiniAppSwitch MiniComponent={NewsPage} WebComponent={NewsPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/pricing' element={
                            <RouteWithSEO
                                titleRu="Тарифы — Флип.Приложения"
                                titleEn="Pricing — Fleep.Apps"
                                descriptionRu="Тарифы и цены на создание мини-приложений"
                                descriptionEn="Pricing plans for creating mini apps"
                                noindex={true}
                            >
                                <MiniAppSwitch MiniComponent={UnderConstructionPage} WebComponent={UnderConstructionPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/reviews' element={
                            <RouteWithSEO
                                titleRu="Отзывы — Флип.Приложения"
                                titleEn="Reviews — Fleep.Apps"
                                descriptionRu="Отзывы пользователей о конструкторе"
                                descriptionEn="User reviews about the builder"
                                noindex={true}
                            >
                                <MiniAppSwitch MiniComponent={UnderConstructionPage} WebComponent={UnderConstructionPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/contacts' element={
                            <RouteWithSEO
                                titleRu="Контакты — Флип.Приложения"
                                titleEn="Contacts — Fleep.Apps"
                                descriptionRu="Свяжитесь с нашей командой"
                                descriptionEn="Contact our team"
                                noindex={true}
                            >
                                <MiniAppSwitch MiniComponent={UnderConstructionPage} WebComponent={UnderConstructionPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/dev' element={
                            <RouteWithSEO
                                titleRu="Разработка — Флип.Приложения"
                                titleEn="Development — Fleep.Apps"
                                descriptionRu="Инструменты разработки"
                                descriptionEn="Development tools"
                                noindex={true}
                            >
                                <MiniAppSwitch MiniComponent={UnderConstructionPage} WebComponent={UnderConstructionPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/services/app'>
                            <Route path='projects' element={
                                <RouteWithSEO
                                    titleRu="Проекты — Флип.Приложения"
                                    titleEn="Projects — Fleep.Apps"
                                    descriptionRu="Управление проектами мини-приложений"
                                    descriptionEn="Manage your mini app projects"
                                    noindex={true}
                                >
                                    <WebLayout>
                                        <UserGuard>
                                            <ProjectsPage />
                                        </UserGuard>
                                    </WebLayout>
                                </RouteWithSEO>
                            } />
                            <Route path='shop/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Магазин — Флип.Приложения"
                                    titleEn="Shop — Fleep.Apps"
                                    descriptionRu="Управление товарами магазина"
                                    descriptionEn="Manage shop products"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ShopPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='shop/:projectId/settings' element={
                                <RouteWithSEO
                                    titleRu="Настройки магазина — Флип.Приложения"
                                    titleEn="Shop Settings — Fleep.Apps"
                                    descriptionRu="Настройки магазина"
                                    descriptionEn="Shop settings"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <StoreSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='theme/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Настройки темы — Флип.Приложения"
                                    titleEn="Theme Settings — Fleep.Apps"
                                    descriptionRu="Настройка темы оформления"
                                    descriptionEn="Customize app theme"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ThemeSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='notifications/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Уведомления — Флип.Приложения"
                                    titleEn="Notifications — Fleep.Apps"
                                    descriptionRu="Настройка уведомлений"
                                    descriptionEn="Configure notifications"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <NotificationsSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='orders/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Заказы — Флип.Приложения"
                                    titleEn="Orders — Fleep.Apps"
                                    descriptionRu="Управление заказами"
                                    descriptionEn="Manage orders"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <OrdersPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='products/:projectId/new' element={
                                <RouteWithSEO
                                    titleRu="Новый товар — Флип.Приложения"
                                    titleEn="New Product — Fleep.Apps"
                                    descriptionRu="Добавление нового товара"
                                    descriptionEn="Add new product"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ProductMobileSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='products/:projectId/:productId/edit' element={
                                <RouteWithSEO
                                    titleRu="Редактирование товара — Флип.Приложения"
                                    titleEn="Edit Product — Fleep.Apps"
                                    descriptionRu="Редактирование товара"
                                    descriptionEn="Edit product"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ProductMobileSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='settings/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Настройки приложения — Флип.Приложения"
                                    titleEn="App Settings — Fleep.Apps"
                                    descriptionRu="Настройки приложения"
                                    descriptionEn="App settings"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <AppSettingsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='editor/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Редактор проекта — Флип.Приложения"
                                    titleEn="Project Editor — Fleep.Apps"
                                    descriptionRu="Редактирование проекта"
                                    descriptionEn="Edit project"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ProjectEditorPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            }>
                                <Route path='screen/:screenId' element={
                                    <UserGuard>
                                        <ScreenEditorPage />
                                    </UserGuard>
                                } />
                            </Route>
                            <Route path='templates' element={
                                <RouteWithSEO
                                    titleRu="Шаблоны — Флип.Приложения"
                                    titleEn="Templates — Fleep.Apps"
                                    descriptionRu="Управление шаблонами"
                                    descriptionEn="Manage templates"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <AdminGuard>
                                                <TemplatesPage />
                                            </AdminGuard>
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='templates/new' element={
                                <RouteWithSEO
                                    titleRu="Новый шаблон — Флип.Приложения"
                                    titleEn="New Template — Fleep.Apps"
                                    descriptionRu="Создание нового шаблона"
                                    descriptionEn="Create new template"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <AdminGuard>
                                                <TemplateCreatePage />
                                            </AdminGuard>
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='templates/:templateId/edit' element={
                                <RouteWithSEO
                                    titleRu="Редактор шаблона — Флип.Приложения"
                                    titleEn="Template Editor — Fleep.Apps"
                                    descriptionRu="Редактирование шаблона"
                                    descriptionEn="Edit template"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <AdminGuard>
                                                <TemplateEditorPage />
                                            </AdminGuard>
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            }>
                                <Route path='screen/:screenId' element={
                                    <UserGuard>
                                        <AdminGuard>
                                            <ScreenEditorPage />
                                        </AdminGuard>
                                    </UserGuard>
                                } />
                            </Route>
                            <Route path='new' element={
                                <RouteWithSEO
                                    titleRu="Новый проект — Флип.Приложения"
                                    titleEn="New Project — Fleep.Apps"
                                    descriptionRu="Создание нового проекта"
                                    descriptionEn="Create new project"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <ProjectCreatePage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='preview/:projectId' element={
                                <AppPreviewPage />
                            } />
                            <Route path='analytics/:projectId' element={
                                <RouteWithSEO
                                    titleRu="Аналитика — Флип.Приложения"
                                    titleEn="Analytics — Fleep.Apps"
                                    descriptionRu="Аналитика и статистика"
                                    descriptionEn="Analytics and statistics"
                                    noindex={true}
                                >
                                    <Layout>
                                        <UserGuard>
                                            <AnalyticsPage />
                                        </UserGuard>
                                    </Layout>
                                </RouteWithSEO>
                            } />
                            <Route path='templates/:templateId/preview' element={
                                <AppPreviewPage />
                            } />
                        </Route>
                        <Route path='/shop/:projectId' element={
                            <RouteWithSEO
                                titleRu="Магазин — Флип.Приложения"
                                titleEn="Shop — Fleep.Apps"
                                descriptionRu="Управление товарами магазина"
                                descriptionEn="Manage shop products"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ShopPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/shop/:projectId/settings' element={
                            <RouteWithSEO
                                titleRu="Настройки магазина — Флип.Приложения"
                                titleEn="Shop Settings — Fleep.Apps"
                                descriptionRu="Настройки магазина"
                                descriptionEn="Shop settings"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <StoreSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/theme/:projectId' element={
                            <RouteWithSEO
                                titleRu="Настройки темы — Флип.Приложения"
                                titleEn="Theme Settings — Fleep.Apps"
                                descriptionRu="Настройка темы оформления"
                                descriptionEn="Customize app theme"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ThemeSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/notifications/:projectId' element={
                            <RouteWithSEO
                                titleRu="Уведомления — Флип.Приложения"
                                titleEn="Notifications — Fleep.Apps"
                                descriptionRu="Настройка уведомлений"
                                descriptionEn="Configure notifications"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <NotificationsSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/orders/:projectId' element={
                            <RouteWithSEO
                                titleRu="Заказы — Флип.Приложения"
                                titleEn="Orders — Fleep.Apps"
                                descriptionRu="Управление заказами"
                                descriptionEn="Manage orders"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <OrdersPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/products/:projectId/new' element={
                            <RouteWithSEO
                                titleRu="Новый товар — Флип.Приложения"
                                titleEn="New Product — Fleep.Apps"
                                descriptionRu="Добавление нового товара"
                                descriptionEn="Add new product"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ProductMobileSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/products/:projectId/:productId/edit' element={
                            <RouteWithSEO
                                titleRu="Редактирование товара — Флип.Приложения"
                                titleEn="Edit Product — Fleep.Apps"
                                descriptionRu="Редактирование товара"
                                descriptionEn="Edit product"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ProductMobileSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/settings/:projectId' element={
                            <RouteWithSEO
                                titleRu="Настройки приложения — Флип.Приложения"
                                titleEn="App Settings — Fleep.Apps"
                                descriptionRu="Настройки приложения"
                                descriptionEn="App settings"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <AppSettingsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/editor/:projectId' element={
                            <RouteWithSEO
                                titleRu="Редактор проекта — Флип.Приложения"
                                titleEn="Project Editor — Fleep.Apps"
                                descriptionRu="Редактирование проекта"
                                descriptionEn="Edit project"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ProjectEditorPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        }>
                            <Route path='screen/:screenId' element={
                                <UserGuard>
                                    <ScreenEditorPage />
                                </UserGuard>
                            } />
                        </Route>
                        <Route path='/templates' element={
                            <RouteWithSEO
                                titleRu="Шаблоны — Флип.Приложения"
                                titleEn="Templates — Fleep.Apps"
                                descriptionRu="Управление шаблонами"
                                descriptionEn="Manage templates"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <AdminGuard>
                                            <TemplatesPage />
                                        </AdminGuard>
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/templates/new' element={
                            <RouteWithSEO
                                titleRu="Новый шаблон — Флип.Приложения"
                                titleEn="New Template — Fleep.Apps"
                                descriptionRu="Создание нового шаблона"
                                descriptionEn="Create new template"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <AdminGuard>
                                            <TemplateCreatePage />
                                        </AdminGuard>
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/templates/:templateId/edit' element={
                            <RouteWithSEO
                                titleRu="Редактор шаблона — Флип.Приложения"
                                titleEn="Template Editor — Fleep.Apps"
                                descriptionRu="Редактирование шаблона"
                                descriptionEn="Edit template"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <AdminGuard>
                                            <TemplateEditorPage />
                                        </AdminGuard>
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        }>
                            <Route path='screen/:screenId' element={
                                <UserGuard>
                                    <AdminGuard>
                                        <ScreenEditorPage />
                                    </AdminGuard>
                                </UserGuard>
                            } />
                        </Route>
                        <Route path='/new' element={
                            <RouteWithSEO
                                titleRu="Новый проект — Флип.Приложения"
                                titleEn="New Project — Fleep.Apps"
                                descriptionRu="Создание нового проекта"
                                descriptionEn="Create new project"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <ProjectCreatePage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/affiliate' element={
                            <RouteWithSEO
                                titleRu="Партнёрская программа конструктора приложений — заработок на Web Apps и Telegram Mini Apps | Fleep"
                                titleEn="Affiliate Program for No-Code App Builder — Earn with Web Apps & Telegram Mini Apps | Fleep"
                                descriptionRu="Присоединяйтесь к партнёрской программе конструктора приложений Fleep. Зарабатывайте на Telegram Mini Apps и Web Apps — получайте доход за каждого клиента."
                                descriptionEn="Join the Fleep affiliate program and earn money promoting a no-code app builder. Get paid for every user of Web Apps and Telegram Mini Apps."
                                noindex={false}
                            >
                                <MiniAppSwitch MiniComponent={AffiliateProgramPage} WebComponent={AffiliateProgramPage} />
                            </RouteWithSEO>
                        } />
                        <Route path='/auth' element={<Navigate to='/?auth=login' replace />} />
                        <Route path='/link-telegram' element={
                            <RouteWithSEO
                                titleRu="Привязка Telegram — Флип.Приложения"
                                titleEn="Link Telegram — Fleep.Apps"
                                descriptionRu="Свяжите аккаунт с Telegram"
                                descriptionEn="Link your Telegram account"
                                noindex={true}
                            >
                                <WebLayout>
                                    <LinkTelegramPage />
                                </WebLayout>
                            </RouteWithSEO>
                        } />
                        <Route path='/preview/:projectId' element={
                            <AppPreviewPage />
                        } />
                        <Route path='/analytics/:projectId' element={
                            <RouteWithSEO
                                titleRu="Аналитика — Флип.Приложения"
                                titleEn="Analytics — Fleep.Apps"
                                descriptionRu="Аналитика и статистика"
                                descriptionEn="Analytics and statistics"
                                noindex={true}
                            >
                                <Layout>
                                    <UserGuard>
                                        <AnalyticsPage />
                                    </UserGuard>
                                </Layout>
                            </RouteWithSEO>
                        } />
                        <Route path='/templates/:templateId/preview' element={
                            <AppPreviewPage />
                        } />
                        <Route path='/app/:projectId/view' element={
                            <AppMiniPage />
                        } />
                        <Route path='/app/:projectId/mobile-settings' element={
                            <RouteWithSEO
                                titleRu="Мобильные настройки — Флип.Приложения"
                                titleEn="Mobile Settings — Fleep.Apps"
                                descriptionRu="Настройки мобильного приложения"
                                descriptionEn="Mobile settings"
                                noindex={true}
                            >
                                <MobileSettings />
                            </RouteWithSEO>
                        } />
                        <Route path='/app/:projectId/settings' element={
                            <RouteWithSEO
                                titleRu="Настройки приложения — Флип.Приложения"
                                titleEn="App Settings — Fleep.Apps"
                                descriptionRu="Настройки приложения"
                                descriptionEn="App settings"
                                noindex={true}
                            >
                                <AppSettingsPage />
                            </RouteWithSEO>
                        } />
                        <Route path='/app/:projectId' element={
                            <AppEntryPage />
                        } />
                        <Route path='*' element={<div>Page not found</div>} />
                    </Routes>
                </Box>
            </Suspense >
        </ThemeProvider>
    );
}

export default AppRouter;
